import { useState, useEffect } from 'react';
import { KnowledgeLesson } from './types';

interface KnowledgePopupProps {
  lesson: KnowledgeLesson;
  onContinue: () => void;
}

const LESSON_IMAGES: Record<number, string> = {
  0: 'https://readdy.ai/api/search-image?query=Cute%20chibi%20Bahnar%20child%20selecting%20correct%20cooking%20tools%20knife%20bowl%20spoon%20water%20firewood%20on%20wooden%20table%2C%20rejecting%20hammer%20toy%20ball%2C%20educational%20cartoon%20illustration%20for%20grade%203%20children%20bright%20cheerful%20colors%20simple%20background&width=200&height=160&seq=lesson-tools-v4&orientation=landscape',
  1: 'https://readdy.ai/api/search-image?query=Cute%20chibi%20Bahnar%20child%20washing%20hands%20step%20by%20step%20open%20tap%20soap%20scrubbing%20rinsing%20drying%20towel%20cartoon%20educational%20illustration%20bright%20blue%20water%20bubbles%20happy%20child&width=200&height=160&seq=lesson-hands-v4&orientation=landscape',
  2: 'https://readdy.ai/api/search-image?query=Cute%20chibi%20Bahnar%20child%20scrubbing%20bamboo%20cooking%20tube%20and%20utensils%20in%20basin%20with%20soap%20and%20water%20bacteria%20disappearing%20cleaning%20tools%20educational%20cartoon%20bright%20colors&width=200&height=160&seq=lesson-tools-wash-v4&orientation=landscape',
  3: 'https://readdy.ai/api/search-image?query=Cute%20chibi%20Bahnar%20child%20rinsing%20sticky%20rice%20in%20bowl%20water%20changing%20from%20cloudy%20to%20clear%20multiple%20rinse%20rounds%20educational%20cartoon%20bright%20illustration%20simple%20background&width=200&height=160&seq=lesson-rice-wash-v4&orientation=landscape',
  4: 'https://readdy.ai/api/search-image?query=Cute%20chibi%20Bahnar%20child%20filling%20bamboo%20tube%20with%20rice%20two%20thirds%20full%20adding%20water%20placing%20banana%20leaf%20on%20top%20adult%20helping%20drill%20hole%20at%20bottom%20safety%20education%20cartoon&width=200&height=160&seq=lesson-tube-prep-v4&orientation=landscape',
  5: 'https://readdy.ai/api/search-image?query=Adult%20Bahnar%20man%20arranging%20firewood%20lighting%20fire%20while%20cute%20chibi%20child%20blows%20gently%20on%20charcoal%20from%20safe%20distance%20fire%20safety%20education%20bright%20warm%20orange%20glow%20cartoon%20illustration&width=200&height=160&seq=lesson-fire-v4&orientation=landscape',
  6: 'https://readdy.ai/api/search-image?query=Bamboo%20rice%20tube%20on%20fire%20with%20thermometer%20showing%20medium%20heat%20golden%20brown%20color%20three%20tubes%20showing%20burned%20black%20correct%20golden%20and%20raw%20white%20outcomes%20educational%20cartoon&width=200&height=160&seq=lesson-cook-v4&orientation=landscape',
  7: 'https://readdy.ai/api/search-image?query=Chibi%20Bahnar%20child%20rotating%20bamboo%20cooking%20tube%20over%20fire%20with%20arrows%20showing%20rotation%20evenly%20cooked%20golden%20brown%20versus%20one%20side%20burned%20black%20uneven%20teaching%20cartoon%20bright%20illustration&width=200&height=160&seq=lesson-rotate-v4&orientation=landscape',
  8: 'https://readdy.ai/api/search-image?query=Happy%20Bahnar%20family%20eating%20golden%20bamboo%20sticky%20rice%20together%20thumb%20up%20healthy%20safe%20cooked%20food%20no%20bacteria%20cartoon%20educational%20illustration%20bright%20warm%20celebration%20scene&width=200&height=160&seq=lesson-finish-v4&orientation=landscape',
};

export default function KnowledgePopup({ lesson, onContinue }: KnowledgePopupProps) {
  const [timeLeft, setTimeLeft] = useState(5);
  const [canContinue, setCanContinue] = useState(false);
  const [reading, setReading] = useState(false);

  useEffect(() => {
    if (reading) return;
    if (timeLeft <= 0) { setCanContinue(true); return; }
    const t = setTimeout(() => setTimeLeft(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [timeLeft, reading]);

  const handleReread = () => {
    setReading(true);
    setTimeLeft(5);
    setCanContinue(false);
    setTimeout(() => setReading(false), 200);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl max-w-sm w-full overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-400 to-amber-400 p-5 text-white text-center">
          <div className="text-4xl mb-2">{lesson.emoji}</div>
          <h3 className="text-base font-extrabold">{lesson.title}</h3>
          <p className="text-xs opacity-75 mt-1">Bài học #{lesson.id + 1}</p>
        </div>

        {/* Content */}
        <div className="p-4">
          <p className="text-sm text-gray-700 leading-relaxed mb-3">{lesson.content}</p>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2">
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-lightbulb-fill text-amber-500 text-sm"></i>
            </div>
            <p className="text-xs font-semibold text-amber-700">{lesson.tip}</p>
          </div>
        </div>

        {/* Timer bar */}
        <div className="px-4">
          <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-orange-400 to-amber-400 rounded-full transition-all duration-1000"
              style={{ width: `${(timeLeft / 5) * 100}%` }} />
          </div>
          {!canContinue && <p className="text-xs text-center text-gray-400 mt-1">Tiếp tục sau {timeLeft}s...</p>}
        </div>

        {/* Actions */}
        <div className="p-4 pt-3 flex gap-3">
          <button onClick={handleReread}
            className="flex-1 py-2.5 border-2 border-amber-300 text-amber-600 font-bold rounded-xl cursor-pointer hover:bg-amber-50 text-sm whitespace-nowrap">
            <i className="ri-repeat-line mr-1"></i>Đọc lại
          </button>
          <button onClick={onContinue} disabled={!canContinue}
            className="flex-1 py-2.5 bg-gradient-to-r from-orange-400 to-amber-400 text-white font-bold rounded-xl cursor-pointer hover:opacity-90 text-sm whitespace-nowrap disabled:opacity-40">
            Tiếp tục <i className="ri-arrow-right-line ml-1"></i>
          </button>
        </div>
      </div>
    </div>
  );
}
